import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Alert, Chip, IconButton, Tooltip,
  Button as MuiButton, Skeleton, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import {
  Search as SearchIcon, OpenInNew as OpenInNewIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  AccessTime as AccessTimeIcon, Storefront as StorefrontIcon,
  VisibilityOff as VisibilityOffIcon, Undo as UndoIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';

// ─── Types ────────────────────────────────────────────────────────────────

interface StoreStat {
  tag: string;
  avgDaysToReceive: number | null;
  daySamples: number;
  received: number;
  lastReceivedAt: string | null;
}

interface StoreGroup {
  storeName: string;
  storeLink: string;
  productCount: number;
  avgDaysToReceive: number | null;
  received: number;
  lastReceivedAt: string | null;
  products: { sku: string; productLink: string }[];
}

// ─── Product links (from Excel upload on Settings page → localStorage) ──────
// productLinksData: [{ id (SKU), link, allLinks[] }]. Per Magnus's sheet,
// allLinks[0] = product page, allLinks[1] = store page.

function parseProductLinks(): Map<string, { link?: string; allLinks?: string[] }> {
  const map = new Map<string, { link?: string; allLinks?: string[] }>();
  try {
    const stored = localStorage.getItem('productLinksData');
    if (!stored) return map;
    const products: any[] = JSON.parse(stored);
    const decode = (s: string) => {
      if (!s) return '';
      const ta = document.createElement('textarea');
      ta.innerHTML = s;
      return ta.value;
    };
    products.forEach(p => {
      if (!p.id) return;
      const allLinks = p.allLinks?.map((l: string) => decode(l)).filter(Boolean) || [];
      const link = decode(p.link || '') || (allLinks.length > 0 ? allLinks[0] : '');
      map.set(String(p.id).trim(), {
        link: link || undefined,
        allLinks: allLinks.length > 0 ? allLinks : undefined,
      });
    });
  } catch (e) {
    console.error('[Stores] Failed to parse productLinksData:', e);
  }
  return map;
}

function lookupLinks(
  map: Map<string, { link?: string; allLinks?: string[] }>, sku: string,
): { link?: string; allLinks?: string[] } {
  if (!sku) return {};
  const skuStr = String(sku).trim();
  let result = map.get(skuStr);
  if (result?.link || result?.allLinks) return result;
  const baseSku = skuStr.split('-')[0].split('_')[0];
  if (baseSku !== skuStr) {
    result = map.get(baseSku);
    if (result?.link || result?.allLinks) return result;
  }
  return {};
}

// ─── Hidden stores (per-browser, localStorage) ──────────────────
// Stores are derived from product 標籤, so there is no store row to delete.
// Hiding is a local view preference — the same place this page already reads
// its store links from, so it survives redeploys.

const HIDDEN_STORES_KEY = 'hiddenStores';

function loadHiddenStores(): Set<string> {
  try {
    const raw = localStorage.getItem(HIDDEN_STORES_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.map((s: any) => String(s).trim()).filter(Boolean));
  } catch (e) {
    console.error('[Stores] Failed to read hiddenStores:', e);
    return new Set();
  }
}

function saveHiddenStores(set: Set<string>): boolean {
  try {
    localStorage.setItem(HIDDEN_STORES_KEY, JSON.stringify(Array.from(set)));
    return true;
  } catch (e) {
    console.error('[Stores] Failed to persist hiddenStores:', e);
    return false;
  }
}

// ─── Sort order (per-browser, localStorage) ─────────────────────────────

type SortOrder = 'slowest' | 'fastest';
const SORT_ORDER_KEY = 'storesSortOrder';

function loadSortOrder(): SortOrder {
  try {
    return localStorage.getItem(SORT_ORDER_KEY) === 'fastest' ? 'fastest' : 'slowest';
  } catch {
    return 'slowest';
  }
}

// ─── Page ───────────────────────────────────────────────────────────────────

const StoresPage: React.FC = () => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const searchRef = useRef<HTMLInputElement>(null);

  const [metaRows, setMetaRows] = useState<any[]>([]);
  const [stats, setStats] = useState<Record<string, StoreStat>>({});
  const [loading, setLoading] = useState(true);
  const [statsLoaded, setStatsLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [batchIndex, setBatchIndex] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [hidden, setHidden] = useState<Set<string>>(() => loadHiddenStores());
  const [showHidden, setShowHidden] = useState(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>(() => loadSortOrder());

  // ── Fetch metadata (store names) + stats (lead time) ──────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [metaRes, statsRes] = await Promise.allSettled([
        api.get('/api/products/metadata'),
        api.get('/api/receiving/store-stats'),
      ]);
      if (cancelled) return;
      if (metaRes.status === 'fulfilled') {
        setMetaRows((metaRes.value as any).data || []);
      } else {
        console.error('[Stores] metadata fetch failed:', (metaRes as any).reason);
        enqueueSnackbar('載入商品標籤失敗', { variant: 'error' });
      }
      if (statsRes.status === 'fulfilled') {
        const map: Record<string, StoreStat> = {};
        ((statsRes.value as any).data || []).forEach((s: StoreStat) => {
          if (s.tag) map[s.tag.trim()] = s;
        });
        setStats(map);
      } else {
        console.error('[Stores] store-stats fetch failed:', (statsRes as any).reason);
      }
      setStatsLoaded(true);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [enqueueSnackbar]);

  useEffect(() => { setBatchIndex(0); }, [search, sortOrder]);

  // ── Group products by 標籤 (store name) ───────────────────────────────────
  const storeGroups = useMemo<StoreGroup[]>(() => {
    const linksMap = parseProductLinks();
    type G = { storeName: string; storeLink: string; skuSet: Set<string>; products: { sku: string; productLink: string }[] };
    const groups = new Map<string, G>();

    metaRows.forEach((m: any) => {
      const tag = String(m.tag || '').trim();
      if (!tag) return;
      const key = String(m.product_key || m.productKey || '');
      const sku = key.split('|')[0].trim();
      if (!sku) return;
      if (!groups.has(tag)) groups.set(tag, { storeName: tag, storeLink: '', skuSet: new Set(), products: [] });
      const g = groups.get(tag)!;
      const links = lookupLinks(linksMap, sku);
      const storeLink = links.allLinks?.[1] || '';
      const productLink = links.allLinks?.[0] || links.link || '';
      if (!g.storeLink && storeLink) g.storeLink = storeLink;
      if (!g.skuSet.has(sku)) {
        g.skuSet.add(sku);
        g.products.push({ sku, productLink });
      }
    });

    let arr: StoreGroup[] = Array.from(groups.values()).map(g => {
      const stat = stats[g.storeName];
      return {
        storeName: g.storeName,
        storeLink: g.storeLink,
        products: g.products.sort((a, b) => a.sku.localeCompare(b.sku)),
        productCount: g.skuSet.size,
        avgDaysToReceive: stat?.avgDaysToReceive ?? null,
        received: stat?.received ?? 0,
        lastReceivedAt: stat?.lastReceivedAt ?? null,
      };
    });

    const q = search.toLowerCase().trim();
    if (q) arr = arr.filter(g => g.storeName.toLowerCase().includes(q));

    return arr;
  }, [metaRows, stats, search]);

  // A store with no link can't be opened, so it isn't actionable here — drop it.
  const linkedStores = useMemo(() => storeGroups.filter(g => g.storeLink), [storeGroups]);
  const noLinkCount = storeGroups.length - linkedStores.length;

  // Lead time asc/desc; stores with no 到貨 data sink to the bottom either way.
  const sortedStores = useMemo(() => {
    const arr = [...linkedStores];
    arr.sort((a, b) => {
      const ad = a.avgDaysToReceive, bd = b.avgDaysToReceive;
      if ((ad == null) !== (bd == null)) return ad == null ? 1 : -1;
      if (ad != null && bd != null && ad !== bd) return sortOrder === 'fastest' ? ad - bd : bd - ad;
      return a.storeName.localeCompare(b.storeName, 'zh-TW');
    });
    return arr;
  }, [linkedStores, sortOrder]);

  const visibleStores = useMemo(
    () => sortedStores.filter(g => !hidden.has(g.storeName)), [sortedStores, hidden]);
  const hiddenStores = useMemo(
    () => sortedStores.filter(g => hidden.has(g.storeName)), [sortedStores, hidden]);

  // ── Hide / restore ────────────────────────────────────────────────────────
  const applyHidden = useCallback((next: Set<string>) => {
    setHidden(next);
    if (!saveHiddenStores(next)) {
      enqueueSnackbar('無法儲存隱藏清單（瀏覽器封鎖了本機儲存）', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  const unhideStore = useCallback((name: string) => {
    setHidden(prev => {
      const next = new Set(prev);
      next.delete(name);
      saveHiddenStores(next);
      return next;
    });
  }, []);

  const hideStore = useCallback((name: string) => {
    setHidden(prev => {
      const next = new Set(prev);
      next.add(name);
      saveHiddenStores(next);
      return next;
    });
    enqueueSnackbar(`已隱藏「${name}」`, {
      variant: 'info',
      autoHideDuration: 6000,
      action: (key) => (
        <MuiButton size="small" startIcon={<UndoIcon sx={{ fontSize: 16 }} />}
          onClick={() => { unhideStore(name); closeSnackbar(key); }}
          sx={{ color: '#fff', textTransform: 'none' }}>
          復原
        </MuiButton>
      ),
    });
  }, [enqueueSnackbar, closeSnackbar, unhideStore]);

  const changeSortOrder = useCallback((next: SortOrder) => {
    setSortOrder(next);
    try { localStorage.setItem(SORT_ORDER_KEY, next); }
    catch (e) { console.error('[Stores] Failed to persist sort order:', e); }
  }, []);

  const restoreAllHidden = useCallback(() => {
    applyHidden(new Set());
    enqueueSnackbar('已復原所有隱藏的店鋪', { variant: 'success' });
  }, [applyHidden, enqueueSnackbar]);

  const remaining = visibleStores.length - batchIndex;
  const batchSize = Math.min(10, Math.max(0, remaining));

  const openBatch = () => {
    const batch = visibleStores.slice(batchIndex, batchIndex + 10);
    batch.forEach((g, i) => setTimeout(() => window.open(g.storeLink, '_blank', 'noopener'), i * 300));
    setBatchIndex(batchIndex + batch.length);
  };

  const toggleExpand = (name: string) => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(name)) next.delete(name); else next.add(name);
    return next;
  });

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box sx={{ px: { xs: 1.5, md: 3 }, pt: 2, pb: 1, maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <StorefrontIcon sx={{ color: '#8b7355', mr: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1c1917' }}>店鋪</Typography>
          {!loading && (
            <Chip label={`${visibleStores.length} 間`} size="small"
              sx={{ ml: 1.5, bgcolor: '#f0f0f0', fontWeight: 500, fontSize: '0.75rem' }} />
          )}
          {!loading && hidden.size > 0 && (
            <Chip
              label={`已隱藏 ${hidden.size} 間`}
              size="small"
              icon={<VisibilityOffIcon sx={{ fontSize: 14 }} />}
              onClick={() => setShowHidden(v => !v)}
              sx={{
                ml: 1, height: 24, fontSize: '0.72rem', cursor: 'pointer',
                bgcolor: showHidden ? '#e8e5e1' : '#f5f3f0', color: '#8b7355',
                '& .MuiChip-icon': { color: 'inherit' },
              }} />
          )}
          <Typography sx={{ fontSize: '0.75rem', color: '#a8a29e', ml: 'auto', display: { xs: 'none', sm: 'block' } }}>
            平均到貨天數 = 下單到收到的平均天數（官網＋Shopify）
          </Typography>
        </Box>

        <TextField
          inputRef={searchRef}
          fullWidth
          size="small"
          placeholder="搜尋店鋪名稱（標籤）..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>
            ),
          }}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: '8px', fontSize: '1rem' } }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ px: { xs: 1.5, md: 3 }, pt: 1, maxWidth: 1200, mx: 'auto' }}>
        {loading ? (
          <Box>
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} variant="rounded" height={64} sx={{ mb: 1.5, borderRadius: '12px' }} />
            ))}
          </Box>
        ) : (
          <>
            {/* Batch-open store links — sticky bar */}
            {visibleStores.length > 0 && (
              <Box sx={{
                mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
                position: 'sticky', top: 0, zIndex: 10,
                bgcolor: 'white', py: 1.5, px: 2,
                borderBottom: '1px solid #e8e5e1', borderRadius: '10px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}>
                <MuiButton variant="outlined" size="small" startIcon={<OpenInNewIcon />} disabled={remaining <= 0}
                  onClick={openBatch}
                  sx={{ textTransform: 'none', borderColor: '#8b7355', color: '#8b7355', '&:hover': { bgcolor: '#f5f3f0', borderColor: '#6b5c42' } }}>
                  開啟下{batchSize}間店鋪 ({batchIndex}/{visibleStores.length})
                </MuiButton>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontSize: '0.8rem', color: '#5c5854' }}>從第</Typography>
                  <TextField
                    type="number"
                    size="small"
                    value={batchIndex}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      if (Number.isNaN(v)) { setBatchIndex(0); return; }
                      setBatchIndex(Math.max(0, Math.min(visibleStores.length, v)));
                    }}
                    inputProps={{ min: 0, max: visibleStores.length, style: { width: 56, textAlign: 'center', padding: '6px 4px' } }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '6px' } }}
                  />
                  <Typography sx={{ fontSize: '0.8rem', color: '#5c5854' }}>間開始 / 共 {visibleStores.length}</Typography>
                </Box>
                {batchIndex > 0 && (
                  <MuiButton size="small" onClick={() => setBatchIndex(0)} sx={{ textTransform: 'none', color: '#8c8580' }}>重置</MuiButton>
                )}
                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '0.78rem', color: '#8c8580' }}>到貨速度</Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={sortOrder}
                    onChange={(_e, v) => { if (v) changeSortOrder(v as SortOrder); }}
                    sx={{
                      '& .MuiToggleButton-root': {
                        textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1.25,
                        color: '#8c8580', borderColor: '#e8e5e1',
                        '&.Mui-selected': {
                          bgcolor: '#f5f3f0', color: '#8b7355', fontWeight: 600,
                          '&:hover': { bgcolor: '#ece8e3' },
                        },
                      },
                    }}
                  >
                    <ToggleButton value="fastest">最快優先</ToggleButton>
                    <ToggleButton value="slowest">最慢優先</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
            )}

            {visibleStores.length === 0 && (
              <Alert severity={noLinkCount > 0 && !search && hidden.size === 0 ? 'warning' : 'info'}
                sx={{ borderRadius: '8px' }}>
                {search
                  ? `找不到符合「${search}」的有連結店鋪`
                  : hidden.size > 0
                    ? '所有店鋪都被隱藏了。點上方「已隱藏」可以復原。'
                    : noLinkCount > 0
                      ? `找到 ${noLinkCount} 間店鋪，但都沒有店鋪連結，所以不顯示。請先到「設定」頁上傳含連結的 Excel。`
                      : '尚無店鋪資料。店鋪名稱來自商品標籤，店鋪連結來自設定頁上傳的 Excel（第 2 個連結）。'}
              </Alert>
            )}

            {/* Hidden stores — restore panel */}
            {showHidden && hidden.size > 0 && (
              <Box sx={{
                mb: 2, p: 2, bgcolor: '#faf9f7', border: '1px dashed #d6d3d1', borderRadius: '12px',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#57534e' }}>
                    已隱藏的店鋪（{hidden.size}）
                  </Typography>
                  <MuiButton size="small" onClick={restoreAllHidden}
                    sx={{ ml: 'auto', textTransform: 'none', color: '#8b7355' }}>
                    全部復原
                  </MuiButton>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {hiddenStores.length === 0 && (
                    <Typography sx={{ fontSize: '0.78rem', color: '#a8a29e' }}>
                      沒有符合目前搜尋的隱藏店鋪。
                    </Typography>
                  )}
                  {hiddenStores.map(store => (
                    <Chip
                      key={store.storeName}
                      label={store.storeName}
                      size="small"
                      onDelete={() => unhideStore(store.storeName)}
                      deleteIcon={<Tooltip title="復原這間店鋪"><UndoIcon sx={{ fontSize: 16 }} /></Tooltip>}
                      sx={{ bgcolor: '#fff', border: '1px solid #e8e5e1', fontSize: '0.75rem' }} />
                  ))}
                </Box>
              </Box>
            )}

            {visibleStores.map(store => {
              const isOpen = expanded.has(store.storeName);
              const avg = store.avgDaysToReceive;
              return (
                <Box key={store.storeName} sx={{
                  mb: 1.5, p: 2, bgcolor: '#fff', border: '1px solid #e8e5e1', borderRadius: '12px',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.98rem', color: '#1c1917' }}>
                      {store.storeName}
                    </Typography>
                    <Chip size="small" label={`${store.productCount} 件商品`}
                      sx={{ height: 22, fontSize: '0.72rem', bgcolor: '#f5f3f0', color: '#57534e' }} />
                    {avg != null ? (
                      <Chip size="small" icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                        label={`平均 ${avg} 天到貨`}
                        sx={{
                          height: 22, fontSize: '0.72rem',
                          bgcolor: avg >= 14 ? '#fdecea' : avg >= 9 ? '#fff4e5' : '#e8f5e9',
                          color: avg >= 14 ? '#c0392b' : avg >= 9 ? '#9a6700' : '#2e7d32',
                          '& .MuiChip-icon': { color: 'inherit' },
                        }} />
                    ) : (
                      <Chip size="small" label="尚無到貨資料"
                        sx={{ height: 22, fontSize: '0.72rem', bgcolor: '#f5f3f0', color: '#a8a29e' }} />
                    )}
                    {store.received > 0 && (
                      <Typography sx={{ fontSize: '0.72rem', color: '#a8a29e' }}>
                        已收 {store.received} 件{store.lastReceivedAt ? `・最後 ${new Date(store.lastReceivedAt).toLocaleDateString('zh-TW')}` : ''}
                      </Typography>
                    )}
                    <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
                      {/* Every listed store has a link — link-less ones are filtered out. */}
                      <MuiButton size="small" variant="outlined" startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
                        onClick={() => window.open(store.storeLink, '_blank', 'noopener')}
                        sx={{ textTransform: 'none', borderColor: '#8b7355', color: '#8b7355', '&:hover': { bgcolor: '#f5f3f0', borderColor: '#6b5c42' } }}>
                        開啟店鋪
                      </MuiButton>
                      <Tooltip title="從清單中移除（可復原）">
                        <IconButton size="small" onClick={() => hideStore(store.storeName)}>
                          <VisibilityOffIcon sx={{ fontSize: 18, color: '#a8a29e' }} />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => toggleExpand(store.storeName)}>
                        {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>

                  {isOpen && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #e8e5e1', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {store.products.map(p => (
                        <Box key={p.sku} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontSize: '0.8rem', color: '#44403c', fontFamily: 'monospace' }}>{p.sku}</Typography>
                          {p.productLink && (
                            <Tooltip title="開啟商品頁面">
                              <IconButton size="small" onClick={() => window.open(p.productLink, '_blank', 'noopener')}>
                                <OpenInNewIcon sx={{ fontSize: 14, color: '#a8a29e' }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </>
        )}
      </Box>
    </Box>
  );
};

export default StoresPage;
